import * as React from 'react';
import { Button, Popover } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { MapGalleryPin } from './MapGalleryPin';

export type MapGalleryMarkerPopupPayload = string | { titleHtml: string; bodyHtml: string };

export interface MapGalleryMarkerPopoverProps {
	popup: MapGalleryMarkerPopupPayload;
	getPopupContainer: () => HTMLElement | null | undefined;
	isOpen: boolean;
	onOpenChange: (open: boolean) => void;
}

function isSplitPayload(
	payload: MapGalleryMarkerPopupPayload
): payload is { titleHtml: string; bodyHtml: string } {
	return (
		typeof payload === 'object' &&
		payload !== null &&
		'titleHtml' in payload &&
		'bodyHtml' in payload
	);
}

/**
 * Click-triggered Ant Design Popover for a map pin. Visibility is controlled by
 * the parent (`isOpen` / `onOpenChange`) so only one popover is open at a time.
 */
const MapGalleryMarkerPopover: React.FC<MapGalleryMarkerPopoverProps> = ({
	popup,
	getPopupContainer,
	isOpen,
	onOpenChange,
}) => {
	const handleClose = (event: React.MouseEvent) => {
		event.stopPropagation();
		onOpenChange(false);
	};

	const closeButton = (
		<Button
			type="text"
			size="small"
			className="mapbox-gallery__marker-popover-closeBtn"
			aria-label="Close"
			icon={<CloseOutlined />}
			onClick={handleClose}
		/>
	);

	const textContent = isSplitPayload(popup) ? (
		<>
			<div
				className="mapbox-gallery__marker-popover-title"
				dangerouslySetInnerHTML={{ __html: popup.titleHtml }}
			/>
			<div
				className="mapbox-gallery__marker-popover-body"
				dangerouslySetInnerHTML={{ __html: popup.bodyHtml }}
			/>
		</>
	) : (
		<div
			className="mapbox-gallery__marker-popover-body"
			dangerouslySetInnerHTML={{ __html: popup }}
		/>
	);

	return (
		<Popover
			visible={isOpen}
			onVisibleChange={onOpenChange}
			trigger="click"
			placement="top"
			autoAdjustOverflow={true}
			mouseEnterDelay={0}
			mouseLeaveDelay={0}
			overlayClassName="mapbox-gallery__marker-popover"
			getPopupContainer={(triggerNode) => {
				const mapEl = getPopupContainer();
				if (mapEl) {
					return mapEl;
				}
				const fallback = triggerNode.closest('.mapbox-gallery');
				if (fallback) {
					return fallback as HTMLElement;
				}
				return document.body;
			}}
			content={
				<div className="mapbox-gallery__marker-popover-inner">
					<div className="mapbox-gallery__marker-popover-content">{textContent}</div>
					<div className="mapbox-gallery__marker-popover-closeCol">{closeButton}</div>
				</div>
			}
		>
			<span
				className="mapbox-gallery__pin-trigger"
				role="button"
				tabIndex={0}
				onKeyDown={(event) => {
					if (event.key === 'Enter' || event.key === ' ') {
						event.preventDefault();
						onOpenChange(!isOpen);
					}
				}}
			>
				<MapGalleryPin />
			</span>
		</Popover>
	);
};

export default MapGalleryMarkerPopover;
